import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import Logo from './components/Logo';
import SignUpPage from './components/SignUpPage';
import LoginPage from './components/LoginPage';
import AboutPage from './components/AboutPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import Footer from './components/Footer';
import { supabase } from './supabaseClient';

function HomePage({
  waitlistEmail,
  setWaitlistEmail,
  floatingEmail,
  setFloatingEmail,
  showFloatingCTA,
  heroSectionRef,
  handleFloatingWaitlist,
  handleHeroWaitlist,
  navigate,
  showMobileProfileMenu,
  setShowMobileProfileMenu,
  showMobileNavMenu,
  setShowMobileNavMenu,
  currentPage,
  location
}) {
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // On mobile: Start video only when fully visible in viewport, reset when less than half visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isMobile) return;

    // Check if IntersectionObserver is supported
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    try {
      // Prevent autoplay on mobile - we'll control it manually
      video.autoplay = false;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.9) {
              // Video is fully visible (at least 90% visible)
              // Start playing if not already playing
              if (video.paused) {
                video.play().catch((error) => {
                  // Silently handle play errors (user interaction may be required)
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('Could not play video:', error);
                  }
                });
              }
            } else if (entry.isIntersecting && entry.intersectionRatio < 0.5) {
              // Video is less than half visible - reset it
              if (!video.paused) {
                video.pause();
              }
              if (video.readyState >= 2) {
                try {
                  video.currentTime = 0;
                } catch (error) {
                  // Silently handle video errors
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('Could not reset video:', error);
                  }
                }
              }
            } else if (!entry.isIntersecting) {
              // Video is completely out of view, pause and reset
              if (!video.paused) {
                video.pause();
              }
              if (video.readyState >= 2) {
                try {
                  video.currentTime = 0;
                } catch (error) {
                  // Silently handle video errors
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('Could not reset video:', error);
                  }
                }
              }
            }
          });
        },
        {
          threshold: [0, 0.5, 0.9, 1.0], // Multiple thresholds to detect visibility levels
        }
      );

      observer.observe(video);

      return () => {
        try {
          observer.disconnect();
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('IntersectionObserver error:', error);
      }
    }
  }, [isMobile]);

  // Reset video when it scrolls out of view (desktop behavior)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || isMobile) return; // Skip on mobile, handled by above effect

    // Check if IntersectionObserver is supported
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    try {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // If video is not intersecting (out of view), reset it
            if (!entry.isIntersecting && video && video.readyState >= 2) {
              try {
                video.currentTime = 0;
              } catch (error) {
                // Silently handle video errors (video might not be loaded yet)
                if (process.env.NODE_ENV === 'development') {
                  console.warn('Could not reset video:', error);
                }
              }
            }
          });
        },
        {
          threshold: 0, // Trigger when any part of the video is out of view
        }
      );

      observer.observe(video);

      return () => {
        try {
          observer.disconnect();
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('IntersectionObserver error:', error);
      }
    }
  }, [isMobile]);

  return (
    <>
      {/* Floating Waitlist Email Input */}
      {showFloatingCTA && (
        <form className="floating-cta-form" onSubmit={handleFloatingWaitlist}>
          <input
            type="email"
            placeholder="Enter your email"
            value={floatingEmail}
            onChange={(e) => setFloatingEmail(e.target.value)}
            className="floating-email-input"
            required
          />
          <button type="submit" className="floating-submit-btn">
            Join →
          </button>
        </form>
      )}

      <div className="App page-transition page-enter-active">
        {/* Navigation */}
        <nav className="navigation">
          <div className="container">
            <div className="nav-content">
              {/* Mobile Hamburger Menu */}
              <div className="mobile-nav-controls">
                <button
                  className={`mobile-hamburger ${showMobileNavMenu ? 'active' : ''}`}
                  onClick={() => setShowMobileNavMenu(!showMobileNavMenu)}
                  aria-label="Navigation menu"
                >
                  {/* Hamburger lines */}
                  <span></span>
                  <span></span>
                  <span></span>
                  {/* Logo icon - appears when active (mobile only) */}
                  <img 
                    className="hamburger-logo-icon"
                    src="/Images/holologo_mobile.svg"
                    alt="DermaMatch Logo"
                  />
                </button>
              </div>

              <div className="nav-logo">
                <button 
                  onClick={() => {
                    if (location.pathname === '/') {
                      // If already on Discover page, scroll to top
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      // Otherwise, navigate to Discover page
                      navigate('/');
                    }
                  }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <Logo size="medium" />
                </button>
              </div>

              <div className="nav-items">
                <button className="nav-link active" onClick={() => navigate('/')}>DISCOVER</button>
                <button className="nav-link" onClick={() => navigate('/about')}>ABOUT</button>
              </div>

              <div className="nav-actions">
                <button className="btn btn-secondary nav-btn" onClick={() => navigate('/login')}>LOG IN</button>
                <button className="btn btn-primary nav-btn" onClick={() => navigate('/signup')}>SIGN UP</button>
              </div>

              {/* Mobile Profile Icon */}
              <button
                className="mobile-profile-icon"
                onClick={() => setShowMobileProfileMenu(!showMobileProfileMenu)}
                aria-label="Profile menu"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Mobile Nav Dropdown */}
            {showMobileNavMenu && (
              <div className="mobile-nav-dropdown">
                <button
                  className={currentPage === 'home' ? 'active' : ''}
                  onClick={() => { navigate('/'); setShowMobileNavMenu(false); }}
                >
                  DISCOVER
                </button>
                <button
                  className={currentPage === 'about' ? 'active' : ''}
                  onClick={() => { navigate('/about'); setShowMobileNavMenu(false); }}
                >
                  ABOUT
                </button>
              </div>
            )}

            {/* Mobile Profile Menu */}
            {showMobileProfileMenu && (
              <div className="mobile-profile-menu">
                <button onClick={() => { navigate('/signup'); setShowMobileProfileMenu(false); }}>
                  SIGN UP
                </button>
                <button onClick={() => { navigate('/login'); setShowMobileProfileMenu(false); }}>
                  LOG IN
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <section className="hero-section" ref={heroSectionRef}>
          <div className="container">
            <div className="hero-content">
              <div className="hero-left">
                <h1 className="hero-heading">
                  The Endless Search
                  <br />
                  For Skincare
                  <br />
                  Stops Here
                </h1>

                <p className="hero-subheading">
                Our AI finds exactly what your skin needs— fast, personalized, and backed by real science. Skip the hassle. Save time, money, and your skin.
                </p>

                <p className="early-access-highlight">Only 1,000 spots for early access + FREE Pro</p>
                <form className="waitlist-container" onSubmit={handleHeroWaitlist}>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    className="waitlist-email-input"
                    required
                  />
                  <button type="submit" className="btn btn-primary btn-large hero-cta">
                    JOIN WAITLIST
                  </button>
                </form>
              </div>

              <div className="hero-right">
                <div className="hero-visual">
                  <video 
                    ref={videoRef}
                    className="hero-video"
                    autoPlay={!isMobile}
                    loop
                    muted
                    playsInline
                    onError={(e) => {
                      // Silently handle video load errors
                      if (process.env.NODE_ENV === 'development') {
                        console.warn('Video load error:', e);
                      }
                    }}
                    onLoadedData={() => {
                      // Video loaded successfully
                    }}
                  >
                    <source src="/Images/Final Website Proto.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* App Features Section */}
        <section className="app-features">
          <div className="container">
            <div className="section-header">
              <h2>Your Perfect Skincare Routine in Seconds</h2>
              <p>The DermaMatch skin assistant delivers personalized skincare recommendations</p>
            </div>

            <div className="features-cards-container">
              <div className="feature-card">
                <div className="feature-card-icon">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="12" y="16" width="56" height="52" rx="6" fill="#EDD682" stroke="#2d3649" strokeWidth="2.5"/>
                    <rect x="12" y="16" width="56" height="14" rx="6" fill="#5592B8" stroke="#2d3649" strokeWidth="2.5"/>
                    <line x1="26" y1="10" x2="26" y2="20" stroke="#2d3649" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="54" y1="10" x2="54" y2="20" stroke="#2d3649" strokeWidth="3" strokeLinecap="round"/>
                    <circle cx="26" cy="42" r="7" fill="#5592B8"/>
                    <circle cx="54" cy="42" r="7" fill="#5592B8"/>
                    <path d="M23.5 42L25.5 44L28.5 41" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M51.5 42L53.5 44L56.5 41" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="feature-card-title">Personalized routine</h3>
                <div className="feature-card-text">
                  <p className="feature-card-description">A full AM and PM routine that fits your skin type, goals, and price range.</p>
                  <p className="feature-card-description secondary">Get one simple routine instead of scrolling through thousands of options.</p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-card-icon">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="24" y="18" width="32" height="48" rx="4" fill="#EDD682" stroke="#2d3649" strokeWidth="2.5"/>
                    <rect x="30" y="10" width="20" height="10" rx="3" fill="#5592B8" stroke="#2d3649" strokeWidth="2.5"/>
                    <circle cx="58" cy="24" r="14" fill="#5592B8" stroke="#2d3649" strokeWidth="2.5"/>
                    <path d="M51 24L55 28L65 18" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="feature-card-title">Unbiased Products</h3>
                <div className="feature-card-text">
                  <p className="feature-card-description">Cross-brand matching driven by ingredients and data, not sponsored slots.</p>
                  <p className="feature-card-description secondary">Stop wasting money on products that do not work.</p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-card-icon">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="40" cy="40" r="30" fill="#EDD682" stroke="#2d3649" strokeWidth="2.5"/>
                    <path d="M40 10 A 30 30 0 0 1 70 40" stroke="#5592B8" strokeWidth="5" fill="none" strokeLinecap="round"/>
                    <circle cx="40" cy="40" r="18" fill="#FFFFFF" stroke="#2d3649" strokeWidth="2.5"/>
                    <circle cx="33" cy="35" r="2.5" fill="#2d3649"/>
                    <circle cx="47" cy="35" r="2.5" fill="#2d3649"/>
                    <path d="M32 48 Q40 52 48 48" stroke="#2d3649" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="feature-card-title">Ongoing Guidance</h3>
                <div className="feature-card-text">
                  <p className="feature-card-description">Monthly scans to adjust products as your skin changes.</p>
                  <p className="feature-card-description secondary">See clear progress with before and after scan visualizations.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section">
          <div className="container">
            <div className="section-header">
              <h2>What People Are Saying</h2>
              <p>Join thousands who are excited about the future of personalized skincare</p>
            </div>

            <div className="testimonials-placeholder">
              <div className="testimonial-card card">
                <div className="testimonial-content">
                  <div className="quote-icon">
                    <svg width="214" height="196" viewBox="0 0 214 196" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M47.2447 63.6352C48.7415 59.0287 55.2585 59.0287 56.7553 63.6353L58.7354 69.7295C59.4048 71.7896 61.3246 73.1844 63.4907 73.1844H69.8986C74.7422 73.1844 76.7561 79.3825 72.8375 82.2295L67.6534 85.9959C65.901 87.2692 65.1677 89.526 65.8371 91.5861L67.8172 97.6804C69.314 102.287 64.0416 106.118 60.123 103.271L54.9389 99.5041C53.1865 98.2308 50.8135 98.2308 49.0611 99.5041L43.877 103.271C39.9584 106.118 34.686 102.287 36.1828 97.6803L38.1629 91.5861C38.8323 89.526 38.099 87.2692 36.3466 85.9959L31.1625 82.2295C27.2439 79.3825 29.2578 73.1844 34.1014 73.1844H40.5093C42.6754 73.1844 44.5952 71.7896 45.2646 69.7295L47.2447 63.6352Z" fill="#EDD682" stroke="black" strokeWidth="5"/>
                      <path d="M139.245 125.635C140.741 121.029 147.259 121.029 148.755 125.635L150.735 131.729C151.405 133.79 153.325 135.184 155.491 135.184H161.899C166.742 135.184 168.756 141.382 164.837 144.229L159.653 147.996C157.901 149.269 157.168 151.526 157.837 153.586L159.817 159.68C161.314 164.287 156.042 168.118 152.123 165.271L146.939 161.504C145.186 160.231 142.814 160.231 141.061 161.504L135.877 165.271C131.958 168.118 126.686 164.287 128.183 159.68L130.163 153.586C130.832 151.526 130.099 149.269 128.347 147.996L123.163 144.229C119.244 141.382 121.258 135.184 126.101 135.184H132.509C134.675 135.184 136.595 133.79 137.265 131.729L139.245 125.635Z" fill="#EDD682" stroke="black" strokeWidth="5"/>
                      <path d="M158.245 63.6352C159.741 59.0287 166.259 59.0287 167.755 63.6353L169.735 69.7295C170.405 71.7896 172.325 73.1844 174.491 73.1844H180.899C185.742 73.1844 187.756 79.3825 183.837 82.2295L178.653 85.9959C176.901 87.2692 176.168 89.526 176.837 91.5861L178.817 97.6804C180.314 102.287 175.042 106.118 171.123 103.271L165.939 99.5041C164.186 98.2308 161.814 98.2308 160.061 99.5041L154.877 103.271C150.958 106.118 145.686 102.287 147.183 97.6803L149.163 91.5861C149.832 89.526 149.099 87.2692 147.347 85.9959L142.163 82.2295C138.244 79.3825 140.258 73.1844 145.101 73.1844H151.509C153.675 73.1844 155.595 71.7896 156.265 69.7295L158.245 63.6352Z" fill="#A7C4D6" stroke="black" strokeWidth="5"/>
                      <path d="M104.245 28.6352C105.741 24.0287 112.259 24.0287 113.755 28.6353L115.735 34.7295C116.405 36.7896 118.325 38.1844 120.491 38.1844H126.899C131.742 38.1844 133.756 44.3825 129.837 47.2295L124.653 50.9959C122.901 52.2692 122.168 54.526 122.837 56.5861L124.817 62.6804C126.314 67.2869 121.042 71.1175 117.123 68.2705L111.939 64.5041C110.186 63.2308 107.814 63.2308 106.061 64.5041L100.877 68.2705C96.9584 71.1175 91.686 67.2869 93.1828 62.6803L95.1629 56.5861C95.8323 54.526 95.099 52.2692 93.3466 50.9959L88.1625 47.2295C84.2439 44.3825 86.2578 38.1844 91.1014 38.1844H97.5093C99.6754 38.1844 101.595 36.7896 102.265 34.7295L104.245 28.6352Z" fill="#EDD682" stroke="black" strokeWidth="5"/>
                      <path d="M69.2447 125.635C70.7415 121.029 77.2585 121.029 78.7553 125.635L80.7354 131.729C81.4048 133.79 83.3246 135.184 85.4907 135.184H91.8986C96.7422 135.184 98.7561 141.382 94.8375 144.229L89.6534 147.996C87.901 149.269 87.1677 151.526 87.8371 153.586L89.8172 159.68C91.314 164.287 86.0416 168.118 82.123 165.271L76.9389 161.504C75.1865 160.231 72.8135 160.231 71.0611 161.504L65.877 165.271C61.9584 168.118 56.686 164.287 58.1828 159.68L60.1629 153.586C60.8323 151.526 60.099 149.269 58.3466 147.996L53.1625 144.229C49.2439 141.382 51.2578 135.184 56.1014 135.184H62.5093C64.6754 135.184 66.5952 133.79 67.2646 131.729L69.2447 125.635Z" fill="#EDD682" stroke="black" strokeWidth="5"/>
                    </svg>
                  </div>
                  <p>Real testimonials from our beta users coming soon!!</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer 
          onNavigateToHome={() => navigate('/')}
          onNavigateToAbout={() => navigate('/about')}
          onNavigateToTerms={() => navigate('/terms')}
          onNavigateToPrivacy={() => navigate('/privacy')}
        />
      </div>
    </>
  );
}

function AppContent() {
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [floatingEmail, setFloatingEmail] = useState('');
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [showMobileProfileMenu, setShowMobileProfileMenu] = useState(false);
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const heroSectionRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll to top on route change and reset state
  useEffect(() => {
    try {
      // Reset scroll position
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }

      // Reset mobile menu states when navigating
      setShowMobileProfileMenu(false);
      setShowMobileNavMenu(false);

      // Reset scroll restoration
      if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (error) {
      // Silently handle errors
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in route change effect:', error);
      }
    }
  }, [location.pathname]);

  // Update floating CTA visibility based on route and scroll
  useEffect(() => {
    const handleScroll = () => {
      try {
        if (location.pathname === '/about') {
          setShowFloatingCTA(true);
        } else if (location.pathname === '/' && heroSectionRef.current) {
          const heroRect = heroSectionRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          if (heroRect.bottom < viewportHeight * 0.3) {
            setShowFloatingCTA(true);
          } else {
            setShowFloatingCTA(false);
          }
        } else {
          setShowFloatingCTA(false);
        }
      } catch (error) {
        // Silently handle scroll errors
        if (process.env.NODE_ENV === 'development') {
          console.warn('Scroll handler error:', error);
        }
      }
    };

    try {
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll(); // Initial check

      return () => {
        try {
          window.removeEventListener('scroll', handleScroll);
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting up scroll listener:', error);
      }
      return () => {};
    }
  }, [location.pathname]);


  // Close mobile menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      try {
        if (!e || !e.target) return;
        
        if (showMobileProfileMenu && !e.target.closest('.mobile-profile-icon') && !e.target.closest('.mobile-profile-menu')) {
          setShowMobileProfileMenu(false);
        }
        if (showMobileNavMenu && !e.target.closest('.mobile-hamburger') && !e.target.closest('.mobile-nav-dropdown')) {
          setShowMobileNavMenu(false);
        }
      } catch (error) {
        // Silently handle errors
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error in click outside handler:', error);
        }
      }
    };

    try {
      document.addEventListener('click', handleClickOutside);
      return () => {
        try {
          document.removeEventListener('click', handleClickOutside);
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting up click outside listener:', error);
      }
      return () => {};
    }
  }, [showMobileProfileMenu, showMobileNavMenu]);

  // Determine current page from location
  const currentPage = location.pathname === '/' ? 'home' : location.pathname.slice(1);

  // Confetti animation
  const createConfetti = () => {
    try {
      if (typeof document === 'undefined' || !document.body) return;

      const colors = ['#EDD682', '#5592B8', '#C8D4E0', '#EAE7D6', '#FFD700', '#87CEEB'];
      const confettiCount = 80;
      const confettiElements = [];

      for (let i = 0; i < confettiCount; i++) {
        try {
          const confetti = document.createElement('div');
          const size = Math.random() * 10 + 5;
          const color = colors[Math.floor(Math.random() * colors.length)];
          const left = Math.random() * 100;
          const animationDuration = Math.random() * 3 + 2;
          const delay = Math.random() * 0.5;
          const rotation = Math.random() * 360;
          const xMovement = (Math.random() - 0.5) * 100;

          confetti.style.cssText = `
            position: fixed;
            width: ${size}px;
            height: ${size}px;
            background-color: ${color};
            left: ${left}%;
            top: -10px;
            z-index: 10000000;
            pointer-events: none;
            border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
            opacity: 1;
            animation: confettiFall ${animationDuration}s ease-out ${delay}s forwards;
            transform: rotate(${rotation}deg);
            --x-movement: ${xMovement}px;
          `;

          if (document.body) {
            document.body.appendChild(confetti);
            confettiElements.push(confetti);
          }
        } catch (error) {
          // Skip this confetti if there's an error
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error creating confetti:', error);
          }
        }
      }

      if (!document.getElementById('confetti-animation') && document.head) {
        try {
          const style = document.createElement('style');
          style.id = 'confetti-animation';
          style.textContent = `
            @keyframes confettiFall {
              0% {
                transform: translateY(0) translateX(0) rotate(0deg);
                opacity: 1;
              }
              100% {
                transform: translateY(100vh) translateX(var(--x-movement)) rotate(720deg);
                opacity: 0;
              }
            }
          `;
          document.head.appendChild(style);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error adding confetti animation:', error);
          }
        }
      }

      setTimeout(() => {
        confettiElements.forEach(confetti => {
          try {
            if (confetti && confetti.parentNode) {
              confetti.parentNode.removeChild(confetti);
            }
          } catch (error) {
            // Silently handle cleanup errors
          }
        });
      }, 5000);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in createConfetti:', error);
      }
    }
  };

  // Show celebration
  const showCelebration = () => {
    try {
      if (typeof document === 'undefined' || !document.body) return;

      createConfetti();

      const celebration = document.createElement('div');
      celebration.innerHTML = `
        <div style="text-align: center;">
          <div style="font-size: 3rem; margin-bottom: 0.5rem;">🎉</div>
          <div style="font-size: 1.3rem; font-weight: 700; margin-bottom: 0.5rem; line-height: 1.3;">Welcome to the family!</div>
          <div style="font-size: 0.95rem; opacity: 0.9; line-height: 1.5;">You're now part of an exclusive community getting early access to personalized skincare powered by AI. We can't wait to help you achieve your best skin yet!</div>
        </div>
      `;
      celebration.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0.8);
        background: linear-gradient(135deg, #2d3649 0%, #3d4659 100%);
        color: #ffffff;
        padding: 2.5rem 2rem;
        border-radius: 16px;
        max-width: 500px;
        width: 90%;
        font-family: 'DM Sans', sans-serif;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        z-index: 9999999;
        pointer-events: none;
        opacity: 0;
        transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      `;

      if (document.body) {
        document.body.appendChild(celebration);

        requestAnimationFrame(() => {
          try {
            if (celebration && celebration.style) {
              celebration.style.opacity = '1';
              celebration.style.transform = 'translate(-50%, -50%) scale(1)';
            }
          } catch (error) {
            // Silently handle animation errors
          }
        });

        setTimeout(() => {
          try {
            if (celebration && celebration.style) {
              celebration.style.opacity = '0';
              celebration.style.transform = 'translate(-50%, -50%) scale(0.8)';
              setTimeout(() => {
                try {
                  if (celebration && celebration.parentNode) {
                    celebration.parentNode.removeChild(celebration);
                  }
                } catch (error) {
                  // Silently handle cleanup errors
                }
              }, 400);
            }
          } catch (error) {
            // Silently handle errors
          }
        }, 4000);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in showCelebration:', error);
      }
    }
  };

  // Show notification
  const showNotification = (message, isError = false) => {
    try {
      if (typeof document === 'undefined' || !document.body) return;
      if (!message) return;

      const notification = document.createElement('div');
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 100px;
        left: 50%;
        transform: translateX(-50%) translateY(-20px);
        background-color: ${isError ? '#d32f2f' : '#2d3649'};
        color: #ffffff;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 0.9rem;
        font-family: 'DM Sans', sans-serif;
        font-weight: 600;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        z-index: 9999999;
        pointer-events: none;
        white-space: nowrap;
        opacity: 0;
        transition: all 0.3s ease;
      `;

      if (document.body) {
        document.body.appendChild(notification);

        requestAnimationFrame(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '1';
              notification.style.transform = 'translateX(-50%) translateY(0)';
            }
          } catch (error) {
            // Silently handle animation errors
          }
        });

        setTimeout(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '0';
              notification.style.transform = 'translateX(-50%) translateY(-20px)';
              setTimeout(() => {
                try {
                  if (notification && notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                  }
                } catch (error) {
                  // Silently handle cleanup errors
                }
              }, 300);
            }
          } catch (error) {
            // Silently handle errors
          }
        }, 2000);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in showNotification:', error);
      }
    }
  };

  // Submit to waitlist
  const submitToWaitlist = async (email) => {
    try {
      // Check if supabase is properly initialized
      if (!supabase || !supabase.from) {
        showNotification('Service temporarily unavailable. Please try again later.', true);
        return false;
      }

      const { error } = await supabase
        .from('waitlist')
        .insert([{ email: email }]);

      if (error) {
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Error adding to waitlist:', error);
        }

        if (error.code === '23505' || error.message.includes('duplicate key')) {
          showNotification('This email is already on the waitlist!', true);
        } else {
          showNotification('Failed to join waitlist. Please try again.', true);
        }
        return false;
      }

      showCelebration();
      return true;
    } catch (err) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Unexpected error:', err);
      }
      showNotification('An error occurred. Please try again.', true);
      return false;
    }
  };

  const handleFloatingWaitlist = async (e) => {
    try {
      e.preventDefault();
      if (floatingEmail.trim()) {
        const success = await submitToWaitlist(floatingEmail.trim());
        if (success) {
          setFloatingEmail('');
        }
      }
    } catch (error) {
      // Silently handle errors - prevent unhandled promise rejections
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in handleFloatingWaitlist:', error);
      }
    }
  };

  const handleHeroWaitlist = async (e) => {
    try {
      e.preventDefault();
      if (waitlistEmail.trim()) {
        const success = await submitToWaitlist(waitlistEmail.trim());
        if (success) {
          setWaitlistEmail('');
        }
      }
    } catch (error) {
      // Silently handle errors - prevent unhandled promise rejections
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in handleHeroWaitlist:', error);
      }
    }
  };

  return (
    <Routes>
      <Route path="/" element={
        <HomePage
          waitlistEmail={waitlistEmail}
          setWaitlistEmail={setWaitlistEmail}
          floatingEmail={floatingEmail}
          setFloatingEmail={setFloatingEmail}
          showFloatingCTA={showFloatingCTA}
          heroSectionRef={heroSectionRef}
          handleFloatingWaitlist={handleFloatingWaitlist}
          handleHeroWaitlist={handleHeroWaitlist}
          navigate={navigate}
          showMobileProfileMenu={showMobileProfileMenu}
          setShowMobileProfileMenu={setShowMobileProfileMenu}
          showMobileNavMenu={showMobileNavMenu}
          setShowMobileNavMenu={setShowMobileNavMenu}
          currentPage={currentPage}
          location={location}
        />
      } />
      <Route path="/about" element={
        <>
          {showFloatingCTA && (
            <form className="floating-cta-form" onSubmit={handleFloatingWaitlist}>
              <input
                type="email"
                placeholder="Enter your email"
                value={floatingEmail}
                onChange={(e) => setFloatingEmail(e.target.value)}
                className="floating-email-input"
                required
              />
              <button type="submit" className="floating-submit-btn">
                Join →
              </button>
            </form>
          )}
          <AboutPage
            onBack={() => navigate('/')}
            onNavigateToLogin={() => navigate('/login')}
            onNavigateToSignup={() => navigate('/signup')}
          />
        </>
      } />
      <Route path="/login" element={
        <LoginPage
          onBack={() => navigate('/')}
          onNavigateToSignup={() => navigate('/signup')}
          onNavigateToAbout={() => navigate('/about')}
          onNavigateToTerms={() => navigate('/terms')}
          onNavigateToPrivacy={() => navigate('/privacy')}
        />
      } />
      <Route path="/signup" element={
        <SignUpPage
          onBack={() => navigate('/')}
          onNavigateToLogin={() => navigate('/login')}
          onNavigateToAbout={() => navigate('/about')}
          onNavigateToTerms={() => navigate('/terms')}
          onNavigateToPrivacy={() => navigate('/privacy')}
        />
      } />
      <Route path="/terms" element={
        <TermsOfServicePage
          onBack={() => navigate('/')}
          onNavigateToLogin={() => navigate('/login')}
          onNavigateToSignup={() => navigate('/signup')}
          onNavigateToAbout={() => navigate('/about')}
          onNavigateToPrivacy={() => navigate('/privacy')}
          showMobileProfileMenu={false}
          setShowMobileProfileMenu={() => {}}
          showMobileNavMenu={false}
          setShowMobileNavMenu={() => {}}
        />
      } />
      <Route path="/privacy" element={
        <PrivacyPolicyPage
          onBack={() => navigate('/')}
          onNavigateToLogin={() => navigate('/login')}
          onNavigateToSignup={() => navigate('/signup')}
          onNavigateToAbout={() => navigate('/about')}
          onNavigateToTerms={() => navigate('/terms')}
          showMobileProfileMenu={false}
          setShowMobileProfileMenu={() => {}}
          showMobileNavMenu={false}
          setShowMobileNavMenu={() => {}}
        />
      } />
      {/* Redirect /landing to home */}
      <Route path="/landing" element={<Navigate to="/" replace />} />
      {/* Catch-all route for undefined paths - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
